import React, { type JSX } from 'react';
import { Tooltip } from 'react-tooltip';
import useElementClicked from '~/lib/segment/useElementClicked';
import styles from './TooltipV2.module.scss';
import LinkWrapper from '../LinkWrapper/linkWrapper';

interface TooltipV2Props {
  text: string | JSX.Element | (JSX.Element | string)[];
  elementPosition?: string;
  className?: string;
  link?: string | string[];
  clickable?: boolean;
}

const TooltipV2 = ({
  text,
  elementPosition,
  className,
  link,
}: TooltipV2Props) => {
  const trackToolTip = useElementClicked(
    'TOOLTIP',
    elementPosition || 'unknown',
    'TOOLTIP_OPENED'
  );

  const renderContent = () => {
    if (typeof text === 'string') {
      let processedText: (string | JSX.Element)[] = [text];

      // Handle bold text
      if (text.includes('_BOLD_')) {
        const boldRegex = /(_BOLD_.*?_BOLD_)/;
        processedText = processedText.flatMap((item) => {
          if (typeof item === 'string' && item.includes('_BOLD_')) {
            const parts = item.split(boldRegex);
            return parts.map((str, index) => {
              if (boldRegex.test(str)) {
                str = str.replaceAll('_BOLD_', '');
                return (
                  <strong key={`bold-${index}`} className={styles.boldText}>
                    {str}
                  </strong>
                );
              }
              return str;
            });
          }
          return item;
        });
      }

      // Handle links
      if (text.includes('_LINK_')) {
        const linkRegex = /(_LINK_[^*].*?_LINK_)/g;
        let linkIndex = -1;
        processedText = processedText.flatMap((item) => {
          if (typeof item === 'string' && item.includes('_LINK_')) {
            const parts = item.split(linkRegex);
            return parts.map((str) => {
              if (linkRegex.test(str)) {
                linkIndex++;
                const noUnderscoreStr = str.replaceAll('_LINK_', '');
                return (
                  <LinkWrapper
                    key={`link-${linkIndex}`}
                    href={
                      Array.isArray(link) ? link[linkIndex] || '' : link || ''
                    }
                    className={styles.link}
                    target='_blank'
                    segmentTracking={{
                      elementName: 'Tooltip Link',
                      elementPosition: elementPosition || 'unknown',
                    }}
                  >
                    {noUnderscoreStr}
                  </LinkWrapper>
                );
              }
              return str;
            });
          }
          return item;
        });
      }

      // Handle superscripts
      if (text.includes('_') && /(_\d+_)|(_\*+_)/.test(text)) {
        const superscriptRegex = /(_\d+_)|(_\*+_)/;
        processedText = processedText.flatMap((item) => {
          if (typeof item === 'string' && item.includes('_')) {
            const parts = item.split(/(_\d+_|_\*+_)/);
            return parts.map((str, index) => {
              if (superscriptRegex.test(str)) {
                str = str.replaceAll('_', '');
                return (
                  <sup key={`sup-${index}`} className={styles.superscript}>
                    {str}
                  </sup>
                );
              }
              return str;
            });
          }
          return item;
        });
      }

      return processedText;
    }
    return text;
  };
  return (
    <span
      role='button'
      tabIndex={0}
      aria-label='Information tooltip'
      onKeyDown={(e) => {
        trackToolTip(e.currentTarget);
      }}
      onClick={(e) => {
        // Stop event propagation to allow tooltip to be clicked without calling onClick within components
        e.stopPropagation();
        trackToolTip(e.currentTarget);
      }}
      onMouseEnter={(e) => {
        trackToolTip(e.currentTarget);
      }}
      className={styles.tooltip}
    >
      <svg
        id={`tooltip-${elementPosition || 'unknown'}`}
        width='17'
        height='16'
        viewBox='0 0 17 16'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        data-tooltip-id={`tooltip-${elementPosition || 'unknown'}`}
        data-tooltip-variant='light'
      >
        <g id={`tooltip-g-outer`}>
          <g id={`tooltip-g-inner`}>
            <path
              d='M7.80984 4.49996C7.43474 4.49996 7.13066 4.79843 7.13066 5.16662L7.13066 5.83329C7.13066 6.20148 7.43474 6.49996 7.80984 6.49996L8.48901 6.49996C8.86411 6.49996 9.16818 6.20148 9.16818 5.83329L9.16818 5.16662C9.16818 4.79843 8.86411 4.49996 8.48901 4.49996L7.80984 4.49996Z'
              className={className}
              id={`tooltip-path-1-`}
            />
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M14.9412 8.66663C14.9412 11.9803 12.2045 14.6666 8.8286 14.6666L7.47025 14.6666C4.09437 14.6666 1.35769 11.9803 1.35769 8.66663L1.35769 7.33329C1.35769 4.01958 4.09437 1.33329 7.47025 1.33329L8.8286 1.33329C12.2045 1.33329 14.9412 4.01958 14.9412 7.33329L14.9412 8.66663ZM8.8286 13.3333L7.47025 13.3333C4.84457 13.3333 2.71603 11.244 2.71603 8.66663L2.71603 7.33329C2.71603 4.75596 4.84457 2.66663 7.47025 2.66663L8.8286 2.66663C11.4543 2.66663 13.5828 4.75596 13.5828 7.33329L13.5828 8.66663C13.5828 11.244 11.4543 13.3333 8.8286 13.3333Z'
              className={className}
              id={`tooltip-path-2-`}
            />
            <path
              d='M8.97717 11.5L7.30576 11.5L7.30576 7.49996L8.97717 7.49996L8.97717 11.5Z'
              className={className}
              id={`tooltip-path-3-`}
            />
          </g>
        </g>
      </svg>

      <Tooltip
        id={`tooltip-${elementPosition || 'unknown'}`}
        className={styles.disclaimer}
        classNameArrow={styles.arrow}
        render={() => <span className='inline-block'>{renderContent()}</span>}
        positionStrategy='fixed'
        clickable={true}
        delayHide={10}
      />
    </span>
  );
};

export default TooltipV2;
